<template>
  <div class="devices-page">
    <!-- Header -->
    <div class="page-header card card-body mb-2">
      <div class="d-flex align-items-center justify-content-between flex-wrap">
        <div>
          <h3 class="mb-0">
            <i class="fa fa-mobile"></i>
            استعراض الأجهزة / الأرقام
          </h3>
          <small class="text-muted">إدارة جميع الأجهزة المسجلة في النظام</small>
        </div>
        <div class="d-flex" style="gap: 8px">
          <button class="btn btn-outline-primary btn-sm" @click="getDevices()">
            <i class="fa fa-refresh"></i> تحديث
          </button>
          <button class="btn btn-primary btn-sm" @click="$router.push('/devices/limits')">
            <i class="fa fa-server"></i> إدارة السيرفرات
          </button>
        </div>
      </div>
    </div>

    <!-- Loading -->
    <div class="card card-body text-center" v-if="loading">
      <p class="mb-0">
        <img
          :src="require('@/assets/images/loading.svg')"
          style="width: 40px; height: 40px"
          alt=""
        />
        جاري التحميل...
      </p>
    </div>

    <!-- KPIs -->
    <div class="row" v-if="!loading">
      <div class="col-6 col-md-3">
        <div class="kpi-card kpi-blue">
          <div class="kpi-icon"><i class="fa fa-mobile"></i></div>
          <div>
            <div class="kpi-label">إجمالي الأجهزة</div>
            <div class="kpi-value">{{ totals.all }}</div>
          </div>
        </div>
      </div>
      <div class="col-6 col-md-3">
        <div class="kpi-card kpi-green">
          <div class="kpi-icon"><i class="fa fa-check-circle"></i></div>
          <div>
            <div class="kpi-label">متصل الآن</div>
            <div class="kpi-value">{{ totals.connected }}</div>
          </div>
        </div>
      </div>
      <div class="col-6 col-md-3">
        <div class="kpi-card kpi-yellow">
          <div class="kpi-icon"><i class="fa fa-clock-o"></i></div>
          <div>
            <div class="kpi-label">لم يتصل بعد</div>
            <div class="kpi-value">{{ totals.pending }}</div>
          </div>
        </div>
      </div>
      <div class="col-6 col-md-3">
        <div class="kpi-card kpi-red">
          <div class="kpi-icon"><i class="fa fa-times-circle"></i></div>
          <div>
            <div class="kpi-label">قُطع الاتصال</div>
            <div class="kpi-value">{{ totals.disconnected }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Workers cards -->
    <div class="row" v-if="!loading && sortedWorkers.length">
      <div class="col-12 col-md-6 col-lg-4" v-for="worker in sortedWorkers" :key="worker.id">
        <div class="worker-card card">
          <div class="worker-card-head">
            <div>
              <span class="worker-tag">Worker</span>
              <span class="worker-id">#{{ worker.id }}</span>
            </div>
            <span class="badge worker-status-badge" :class="workerHealthClass(worker)">
              {{ workerHealthLabel(worker) }}
            </span>
          </div>

          <div class="worker-stats">
            <div class="stat">
              <span class="stat-dot bg-success"></span>
              <span class="stat-label">متصل</span>
              <span class="stat-value">{{ worker.status_1 }}</span>
            </div>
            <div class="stat">
              <span class="stat-dot bg-warning"></span>
              <span class="stat-label">لم يتصل</span>
              <span class="stat-value">{{ worker.status_0 }}</span>
            </div>
            <div class="stat">
              <span class="stat-dot bg-danger"></span>
              <span class="stat-label">انقطع</span>
              <span class="stat-value">{{ worker.status_2 }}</span>
            </div>
          </div>

          <div class="worker-usage">
            <template v-if="limitsMap[worker.id]">
              <div class="d-flex justify-content-between mb-1">
                <small>الاستخدام: <b>{{ worker.total }}</b> / {{ limitsMap[worker.id].max }}</small>
                <small><b>{{ pct(worker.total, limitsMap[worker.id].max) }}%</b></small>
              </div>
              <div class="progress" style="height: 10px; border-radius: 6px">
                <div
                  class="progress-bar"
                  :class="barClass(worker.total, limitsMap[worker.id].max)"
                  :style="{ width: pct(worker.total, limitsMap[worker.id].max) + '%' }"
                ></div>
              </div>
            </template>
            <template v-else>
              <div class="d-flex justify-content-between mb-1">
                <small>الإجمالي: <b>{{ worker.total }}</b></small>
                <small class="text-muted">لا يوجد حد أقصى</small>
              </div>
              <div class="progress" style="height: 10px; border-radius: 6px">
                <div class="progress-bar bg-secondary" style="width: 100%; opacity: 0.2"></div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>

    <!-- Devices table -->
    <div class="card" v-if="!loading">
      <div class="card-body">
        <div class="d-flex align-items-center justify-content-between mb-2 flex-wrap" style="gap: 8px">
          <div>
            <h5 class="mb-0">قائمة الأجهزة</h5>
            <small class="text-muted">{{ filteredDevices.length }} جهاز</small>
          </div>
          <div class="search-box">
            <i class="fa fa-search"></i>
            <input
              type="text"
              class="form-control"
              placeholder="ابحث برقم الجهاز أو رقم المستخدم..."
              @keyup="enablesearch = false"
              @change="enablesearch = true"
              v-model="search"
            />
          </div>
        </div>
        <p class="text-muted small mb-2">
          <i class="fa fa-info-circle"></i>
          اكتب البحث واضغط في مكان فارغ للتنفيذ، ولعرض الكل ابحث عن مسافة.
        </p>

        <div class="table-responsive">
          <table class="table table-hover devices-table">
            <thead>
              <tr>
                <th>المستخدم</th>
                <th>الجهاز</th>
                <th>الحالة</th>
                <th>التفاصيل</th>
                <th>تاريخ الإضافة</th>
                <th class="text-center">خيارات</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="device in visibleDevices"
                :key="device._id"
              >
                <td>
                  <a
                    class="user-link"
                    @click="$router.push('/users#' + users[device.user_id]._id)"
                  >
                    <div class="user-name">{{ users[device.user_id].name }}</div>
                    <div class="user-phone">{{ users[device.user_id].phone }}</div>
                  </a>
                </td>
                <td>
                  <div class="device-cell">
                    <img
                      v-if="device.info"
                      class="device-img"
                      :src="device.info.image"
                      @error="
                        device.info.image =
                          'https://cdn-static.brmja.com/storage/uploads/images/63871906bdfcc560647582.webp'
                      "
                      alt=""
                    />
                    <div>
                      <div class="device-title">{{ device.title }}</div>
                      <span class="badge slow-badge" v-if="device.slow_mode">
                        <i class="fa fa-shield"></i> وضع الحماية
                      </span>
                    </div>
                  </div>
                </td>
                <td>
                  <span class="status-pill status-pending" v-if="device.status == 0">
                    <i class="fa fa-clock-o"></i> لم يتصل بعد
                  </span>
                  <span class="status-pill status-connected" v-if="device.status == 1">
                    <i class="fa fa-check"></i> متصل الآن
                  </span>
                  <span class="status-pill status-disconnected" v-if="device.status == 2">
                    <i class="fa fa-times"></i> قُطع الاتصال
                  </span>
                  <span class="status-pill status-meta" v-if="device.status == 10">
                    <i class="fa fa-bolt"></i> Meta API
                  </span>
                </td>
                <td>
                  <div class="meta-line" v-if="device.phone">
                    <i class="fa fa-phone"></i> {{ device.phone }}
                  </div>
                  <div class="meta-line" v-if="device.info && device.info.platform">
                    <i class="fa fa-desktop"></i> {{ device.info.platform }}
                  </div>
                  <div class="meta-line">
                    <span class="worker-chip">
                      <i class="fa fa-server"></i> Worker #{{ device.worker }}
                    </span>
                  </div>
                </td>
                <td>
                  <span class="date-pill">
                    <i class="fa fa-calendar"></i>
                    {{ device.add_date.split("T")[0] }}
                  </span>
                </td>
                <td class="text-center">
                  <b-dropdown
                    id="dropdown-1"
                    v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                    text="خيارات"
                    variant="primary"
                    size="sm"
                  >
                    <b-dropdown-item
                      @click="restart(device._id)"
                      v-if="device.status == 1"
                    >
                      <i class="fa fa-refresh"></i> اعادة تشغيل
                    </b-dropdown-item>
                    <b-dropdown-item @click="kick(device._id)">
                      <i class="fa fa-ban"></i> تعطيل/طرد
                    </b-dropdown-item>
                    <b-dropdown-item @click="idd(device._id)">
                      <i class="fa fa-key"></i> Device ID
                    </b-dropdown-item>
                    <b-dropdown-item @click="deleteDevice(device._id)">
                      <i class="fa fa-trash"></i> حذف
                    </b-dropdown-item>
                  </b-dropdown>
                </td>
              </tr>
              <tr v-if="!visibleDevices.length">
                <td colspan="6" class="text-center text-muted py-4">
                  <i class="fa fa-inbox" style="font-size: 28px; opacity: 0.4"></i>
                  <div>لا توجد أجهزة مطابقة</div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { BDropdown, BDropdownItem } from "bootstrap-vue";
import Ripple from "vue-ripple-directive";
export default {
  components: {
    BDropdown,
    BDropdownItem,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      current: {},
      enablesearch: false,
      search: "",
      devices: [],
      users: {},
      workers: [],
      limitsMap: {},
      loading: true,
      user: JSON.parse(localStorage.getItem("user")),
    };
  },
  computed: {
    sortedWorkers() {
      return [...this.workers].sort((a, b) => Number(a.id) - Number(b.id));
    },
    totals() {
      var t = { all: 0, connected: 0, pending: 0, disconnected: 0 };
      this.workers.forEach(function (w) {
        t.all += w.total || 0;
        t.connected += w.status_1 || 0;
        t.pending += w.status_0 || 0;
        t.disconnected += w.status_2 || 0;
      });
      return t;
    },
    filteredDevices() {
      var g = this;
      if (g.search == "") return g.devices;
      return g.devices.filter(function (device) {
        var u = g.users[device.user_id] || {};
        return (
          (u.phone && u.phone.includes(g.search)) ||
          (device.phone && device.phone.includes(g.search))
        );
      });
    },
    visibleDevices() {
      if (this.enablesearch || this.search != "") return this.filteredDevices;
      return this.devices.slice(0, 50);
    },
  },
  created() {
    var g = this;
    $.post(api + "/admin/devices/limits/list", {
      jwt: g.user.jwt,
    }).then(function (r) {
      var m = {};
      (r.response.limits || []).forEach(function (l) {
        m[l.worker_id] = l;
      });
      g.limitsMap = m;
    });
    $.post(api + "/admin/users/list", {
      jwt: g.user.jwt,
    }).then(function (r) {
      r.response.forEach((element) => {
        g.users[element._id] = element;
      });
      g.getDevices();
    });
  },
  methods: {
    pct(used, max) {
      if (!max) return 0;
      return Math.min(100, Math.round((used / max) * 100));
    },
    barClass(used, max) {
      var p = this.pct(used, max);
      if (p < 50) return "bg-success";
      if (p < 80) return "bg-warning";
      return "bg-danger";
    },
    workerHealthClass(worker) {
      var max = this.limitsMap[worker.id] && this.limitsMap[worker.id].max;
      if (!max) return "bg-secondary";
      var p = this.pct(worker.total, max);
      if (p < 50) return "bg-success";
      if (p < 80) return "bg-warning";
      return "bg-danger";
    },
    workerHealthLabel(worker) {
      var max = this.limitsMap[worker.id] && this.limitsMap[worker.id].max;
      if (!max) return "غير محدد";
      var p = this.pct(worker.total, max);
      if (p < 50) return "جيد";
      if (p < 80) return "متوسط";
      return "ممتلئ";
    },
    getDevices() {
      var g = this;
      g.loading = true;
      g.workers = [];
      $.post(api + "/admin/devices/list", {
        jwt: this.user.jwt,
      })
        .then(function (r) {
          g.loading = false;
          var arr = [];
          r.response.forEach(function (a) {
            var found = false;
            g.workers.forEach(function (w) {
              if (w.id == a.worker) {
                found = true;
              }
            });
            if (!found) {
              g.workers.push({
                id: a.worker,
                total: 0,
                status_0: 0,
                status_1: 0,
                status_2: 0,
              });
            }
            var workers = [];
            g.workers.forEach(function (w) {
              if (w.id == a.worker) {
                w.total = w.total + 1;
                if (!w["status_" + a.status]) {
                  w["status_" + a.status] = 0;
                }
                w["status_" + a.status] = w["status_" + a.status] + 1;
              }
              workers.push(w);
            });
            g.workers = workers;
            if (a.user_id == window.location.hash.split("#")[1] || !window.location.hash) {
              arr.push(a);
            }
          });
          g.devices = arr;
        })
        .catch(function () {
          alert("error server or internet");
        });
    },
    deleteDevice(id) {
      var g = this;
      if (confirm("متأكد من حذف الجهاز؟")) {
        $.post(api + "/admin/devices/delete", {
          jwt: this.user.jwt,
          id: id,
        })
          .then(function (r) {
            g.getDevices();
          })
          .catch(function () {
            alert("error server or internet");
          });
      }
    },
    idd(id) {
      prompt("id:", id);
    },
    restart(id) {
      var g = this;
      if (confirm("متأكد من اعادة تشغيل الخدمة؟")) {
        $.post(api + "/admin/devices/restart", {
          jwt: g.user.jwt,
          id: id,
        })
          .then(function () {
            g.getDevices();
          })
          .catch(function () {
            alert("حدث خطأ");
          });
      }
    },
  },
};
</script>

<style scoped>
.devices-page {
  padding: 0;
}

.page-header h3 {
  font-weight: 700;
}
.page-header h3 .fa {
  color: #6366f1;
  margin-left: 4px;
}

/* KPI cards */
.kpi-card {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px 18px;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  margin-bottom: 16px;
  border: 1px solid rgba(0, 0, 0, 0.04);
}
.kpi-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  color: #fff;
}
.kpi-label {
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 2px;
}
.kpi-value {
  font-size: 22px;
  font-weight: 700;
  color: #111827;
  line-height: 1;
}
.kpi-blue .kpi-icon { background: linear-gradient(135deg, #6366f1, #8b5cf6); }
.kpi-green .kpi-icon { background: linear-gradient(135deg, #10b981, #34d399); }
.kpi-yellow .kpi-icon { background: linear-gradient(135deg, #f59e0b, #fbbf24); }
.kpi-red .kpi-icon { background: linear-gradient(135deg, #ef4444, #f87171); }

/* Worker cards */
.worker-card {
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.05);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.04);
  margin-bottom: 16px;
  padding: 16px;
  transition: transform 0.15s ease;
}
.worker-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08);
}
.worker-card-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}
.worker-tag {
  font-size: 11px;
  background: #eef2ff;
  color: #4f46e5;
  padding: 2px 8px;
  border-radius: 6px;
  font-weight: 600;
}
.worker-id {
  font-weight: 700;
  font-size: 18px;
  margin-right: 6px;
  color: #111827;
}
.worker-status-badge {
  font-size: 11px;
  padding: 4px 8px;
  border-radius: 6px;
}
.worker-stats {
  display: flex;
  justify-content: space-between;
  background: #f9fafb;
  padding: 10px 12px;
  border-radius: 10px;
  margin-bottom: 12px;
}
.stat {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
}
.stat-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
}
.stat-label {
  color: #6b7280;
}
.stat-value {
  font-weight: 700;
  color: #111827;
}

/* Search */
.search-box {
  position: relative;
  width: 320px;
  max-width: 100%;
}
.search-box .fa {
  position: absolute;
  top: 50%;
  right: 12px;
  transform: translateY(-50%);
  color: #9ca3af;
}
.search-box input {
  padding-right: 34px;
  border-radius: 10px;
}

/* Devices table */
.devices-table {
  margin-bottom: 0;
}
.devices-table thead th {
  background: #f9fafb;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #6b7280;
  border-bottom: 1px solid #e5e7eb;
  border-top: none;
  padding: 12px;
}
.devices-table tbody td {
  vertical-align: middle;
  padding: 12px;
  border-top: 1px solid #f3f4f6;
}

.user-link {
  cursor: pointer;
  color: #047857;
  display: block;
}
.user-link:hover .user-name {
  text-decoration: underline;
}
.user-name {
  font-weight: 600;
  color: #047857;
}
.user-phone {
  font-size: 12px;
  color: #6b7280;
  direction: ltr;
  text-align: right;
}

.device-cell {
  display: flex;
  align-items: center;
  gap: 10px;
}
.device-img {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #f3f4f6;
}
.device-title {
  font-weight: 600;
  color: #111827;
}
.slow-badge {
  background: #62109F !important;
  color: #fff;
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 4px;
  margin-top: 4px;
  display: inline-block;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
}
.status-connected {
  background: #d1fae5;
  color: #065f46;
}
.status-pending {
  background: #fef3c7;
  color: #92400e;
}
.status-disconnected {
  background: #fee2e2;
  color: #991b1b;
}
.status-meta {
  background: #ede9fe;
  color: #5b21b6;
}

.meta-line {
  font-size: 13px;
  color: #4b5563;
  margin-bottom: 2px;
}
.meta-line .fa {
  color: #9ca3af;
  margin-left: 4px;
}
.worker-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: #eef2ff;
  color: #4f46e5;
  padding: 3px 8px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  margin-top: 2px;
}
.date-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #f3f4f6;
  color: #4b5563;
  padding: 4px 10px;
  border-radius: 8px;
  font-size: 12px;
}
</style>
